import Phaser from 'phaser';
import { CardTransition } from '../objects/CardTransition';
import { applyRenderScale } from '../renderScale';

interface HandPosition {
  x: number;
  y: number;
  rotation: number;
}

/**
 * Overlay scene that manages card transitions across scene switches.
 * Renders on top of other scenes and persists during LobbyScene→GameScene transition.
 */
export class TransitionOverlayScene extends Phaser.Scene {
  private cardTransition?: CardTransition;

  constructor() {
    super({ key: 'TransitionOverlayScene' });
  }

  create(): void {
    applyRenderScale(this);
    this.cardTransition = new CardTransition(this);
  }

  /**
   * Start the scatter animation for game start transition.
   * @param onMidPoint - Called when cards reach mid-scatter (time to switch scenes)
   */
  startScatter(onMidPoint?: () => void): void {
    this.cardTransition?.scatterPartial(onMidPoint);
  }

  /**
   * Continue cards to hand positions after scene switch.
   * @param positions - Target positions for each card in the hand
   * @param cardKeys - Texture keys for card faces (for reveal)
   * @param onComplete - Called when animation is complete
   */
  continueToHand(positions: HandPosition[], cardKeys: string[], onComplete?: () => void): void {
    if (!this.cardTransition) return;

    // First animate to hand positions
    this.cardTransition.continueToPositions(positions, () => {
      // Then reveal the cards
      this.cardTransition?.revealCards(cardKeys, () => {
        onComplete?.();
      });
    });
  }

  /**
   * Check if the overlay has an active transition.
   */
  isActive(): boolean {
    return this.cardTransition !== undefined && this.cardTransition.getCards().length > 0;
  }

  /**
   * Clean up the transition and stop the overlay scene.
   */
  cleanup(): void {
    this.cardTransition?.destroy();
    this.cardTransition = undefined;
    this.scene.stop('TransitionOverlayScene');
  }
}
